// @file Slug → human-readable label for Arcade generation types.
//
// Keys are `GenerationType` enum values (services/sondheim/src/enums.ts) and the labels mirror
// Sondheim's `mapGenerationTypeToTitle` (services/sondheim/src/utils/shared/generationTypeMapping.ts).
// Keep the two in sync until codegen from Sondheim replaces this map.
//
// This is the source of the type label *displayed* on attachment chiclets: it resolves the persisted,
// locale-independent `arcade_game_type` slug into a label in the *viewer's* locale at render time. The
// `arcade_game_type_label` persisted on a post was resolved in the *author's* locale at attach time, so
// it must not drive display — it is only a last-resort fallback for a slug this map doesn't know yet.

import { __ } from '@@/bits/intl'

const ARCADE_GAME_TYPE_LABELS = {
  'activity-ideas': __('Class activity ideas'),
  'class-activities': __('Class activity ideas'),
  'coloring-sheet': __('Coloring'),
  crossword: __('Crossword'),
  curriculum: __('Curriculum outline'),
  'decodable-book': __('Decodable text book'),
  'fill-in-the-blank': __('Fill in the blank'),
  flashcards: __('Flashcards'),
  // Sondheim has no `game` case — canonical Arcade share links use a generic `/game/<id>` path with
  // no specific activity type, so the parsed slug is the bare "game". Rails-only friendly label.
  game: __('Arcade game'),
  grading: __('Grading'),
  'image-generator': __('AI image'),
  'lesson-plan': __('Lesson plan'),
  matching: __('Matching'),
  'memory-tiles': __('Memory tiles'),
  'multimedia-presentation': __('Presentation'),
  'multiple-choice-quiz': __('Multiple choice'),
  'multiple-choice-v2': __('Multiple choice'),
  'name-picker': __('Name picker'),
  presentation: __('Presentation'),
  'procedural-text': __('Procedural text'),
  'qr-code-generator': __('QR code'),
  'reading-passage': __('Reading passage'),
  rubric: __('Rubric'),
  sequencing: __('Sequencing'),
  'short-answer': __('Short answer'),
  'short-link': __('Short link'),
  'short-story': __('Short story'),
  'smart-groups': __('Smart groups'),
  sorting: __('Sorting'),
  studio: __('Wildcard'),
  'text-leveler': __('Text leveler'),
  'text-translator': __('Text translator'),
  'tracing-sheet': __('Tracing'),
  'word-problem': __('Word problem'),
  'word-search': __('Word search'),
  worksheet: __('Worksheet'),
} satisfies Record<string, string>

type ArcadeGameType = keyof typeof ARCADE_GAME_TYPE_LABELS

// Returns the viewer-locale label for a known slug, or `undefined` when the slug is empty or unknown
// so callers can fall back (to the persisted label, then the raw slug) rather than show a bare slug.
function getArcadeGameTypeLabel(slug: string): string | undefined {
  if (slug === '') return undefined
  return ARCADE_GAME_TYPE_LABELS[slug as ArcadeGameType]
}

// The single resolution every render site shares, so the fallback order lives in one place instead of
// being re-spelled at each call. Prefers the viewer-locale label from the slug; falls back to the
// persisted (author-locale) label for a slug the map doesn't know yet, then to the raw slug. Returns
// `undefined` only when there is nothing to show — callers coerce to '' where a string is required.
function resolveArcadeGameTypeLabel(
  slug: string,
  persistedLabel?: string | null,
): string | undefined {
  // Use `||` (not `??`) for the fallbacks: an empty-string persisted label or slug must fall through
  // — to the raw slug, then to the documented "nothing to show" `undefined` — rather than surface as
  // a bare empty string. Known-slug labels are always non-empty, so `||` never discards a real value.
  return getArcadeGameTypeLabel(slug) || persistedLabel || slug || undefined
}

export { getArcadeGameTypeLabel, resolveArcadeGameTypeLabel }
export type { ArcadeGameType }
